import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Lightbulb,
  LogOut,
  User,
  ArrowLeft,
  Download,
  TrendingUp,
  BarChart3,
  PieChart,
  Users as UsersIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, PieChart as RechartsPie, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#EA580C', '#F97316', '#FB923C', '#FDBA74', '#FED7AA', '#2563EB', '#10B981', '#8B5CF6'];

const Analytics = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [categoryData, setCategoryData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [priorityData, setPriorityData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [timeData, setTimeData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [categoryRes, statusRes, priorityRes, teamRes, timeRes] = await Promise.all([
        axios.get(`${API}/analytics/category-breakdown`),
        axios.get(`${API}/analytics/status-distribution`),
        axios.get(`${API}/analytics/priority-breakdown`),
        axios.get(`${API}/analytics/team-contributions`),
        axios.get(`${API}/analytics/ideas-over-time`)
      ]);

      setCategoryData(categoryRes.data);
      setStatusData(statusRes.data);
      setPriorityData(priorityRes.data);
      setTeamData(teamRes.data);
      setTimeData(timeRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await axios.get(`${API}/export/ideas?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json'
      });

      if (format === 'csv') {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ideas-export-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        const dataStr = JSON.stringify(response.data, null, 2);
        const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
        const link = document.createElement('a');
        link.setAttribute('href', dataUri);
        link.setAttribute('download', `ideas-export-${new Date().toISOString().split('T')[0]}.json`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] dark:bg-zinc-900">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-zinc-800/60">
        <div className="container mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-button"
              className="text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg p-2 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-[#5B6B8F] flex items-center justify-center">
                <Lightbulb className="h-5 w-5 text-white" />
              </div>
              <span className="text-xl font-bold text-zinc-900 dark:text-white">GBA Idea App Analytics</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleExport('json')}
              data-testid="export-json-button"
              className="flex items-center gap-2 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <Download size={16} />
              JSON
            </button>
            <button
              onClick={() => handleExport('csv')}
              data-testid="export-csv-button"
              className="flex items-center gap-2 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors"
            >
              <Download size={16} />
              CSV
            </button>
            <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 dark:bg-zinc-700 rounded-lg border border-zinc-200 dark:border-zinc-600">
              <User size={16} className="text-zinc-500 dark:text-zinc-400" />
              <span className="text-sm font-medium text-zinc-900 dark:text-white">{user.name}</span>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-700 hover:text-zinc-900 dark:hover:text-white rounded-lg p-2 transition-colors"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {loading ? (
          <div className="text-center py-12 text-zinc-500 dark:text-zinc-400">Loading analytics...</div>
        ) : (
          <div className="space-y-8">
            {/* Category Breakdown */}
            <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="category-chart">
              <div className="flex items-center gap-2 mb-6">
                <BarChart3 className="text-[#5B6B8F]" size={20} />
                <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Ideas by Category</h2>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                  <XAxis dataKey="category" stroke="#71717A" />
                  <YAxis stroke="#71717A" />
                  <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="#EA580C" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Status Distribution */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="status-chart">
                <div className="flex items-center gap-2 mb-6">
                  <PieChart className="text-[#5B6B8F]" size={20} />
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Status Distribution</h2>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={statusData}
                      dataKey="count"
                      nameKey="status"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '8px' }} />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>

              {/* Priority Breakdown */}
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="priority-chart">
                <div className="flex items-center gap-2 mb-6">
                  <TrendingUp className="text-[#5B6B8F]" size={20} />
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Priority Breakdown</h2>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={priorityData}
                      dataKey="count"
                      nameKey="priority"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label
                    >
                      {priorityData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '8px' }} />
                    <Legend />
                  </RechartsPie>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Team Contributions */}
            {teamData.length > 0 && (
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="team-chart">
                <div className="flex items-center gap-2 mb-6">
                  <UsersIcon className="text-[#5B6B8F]" size={20} />
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Team Contributions</h2>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={teamData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="user_name" stroke="#71717A" />
                    <YAxis stroke="#71717A" />
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '8px' }} />
                    <Bar dataKey="count" fill="#10B981" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Ideas Over Time */}
            {timeData.length > 0 && (
              <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 shadow-sm" data-testid="time-chart">
                <div className="flex items-center gap-2 mb-6">
                  <TrendingUp className="text-[#5B6B8F]" size={20} />
                  <h2 className="text-xl font-bold text-zinc-900 dark:text-white">Ideas Created Over Time</h2>
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={timeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
                    <XAxis dataKey="date" stroke="#71717A" />
                    <YAxis stroke="#71717A" />
                    <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E4E4E7', borderRadius: '8px' }} />
                    <Line type="monotone" dataKey="count" stroke="#EA580C" strokeWidth={2} dot={{ fill: '#EA580C' }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
